import React from "react";
import { Link } from "react-router-dom";
import { formatBranchMoney } from "@/lib/api";
import { openReceipt, emailReceipt } from "@/lib/receipt";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, Printer, Mail, PackagePlus } from "lucide-react";
import LoyaltyPointsPanel from "./LoyaltyPointsPanel";
import TreatmentDateEditor from "./TreatmentDateEditor";
import { PaymentStatusPill } from "./StatusPill";
import { fmtDate, fmtDateTime } from "./dateFmt";
import CheckInDialog from "./CheckInDialog";
import { CheckCircle2, UserCheck } from "lucide-react";

/* ---------------- OrderHeaderCard ----------------
 *  Top card of the OrderDetail page: package banner, guest identity
 *  (name + phone/email + Member badge + loyalty chip), order metadata
 *  (number/created/treatment/last-payment/total/paid/balance/status),
 *  progress bar, header actions (Save For Later, Print / Email
 *  Receipt, Void / Refund on completed orders + closed-state badges).
 *
 *  Iter-365 (Feb 2026): legacy "Send / Resend Invite" affordance
 *  DELETED. The Guest is now activated to a Member the moment their
 *  first OTP sign-in lands (Iter-363) — staff simply direct the guest
 *  to /portal/login and the backend flips them to Member automatically.
 *  The Pending Member badge below is retained as a visual indicator.
 *
 *  Kept props-only so the parent still owns the `refresh()` cycle
 *  after every server-side mutation.
 * ------------------------------------------------------------------ */
export default function OrderHeaderCard({
  order, branch, customerDetail,
  onClose, refresh, scrollToHistory,
  total, paid, due, progressPct,
  businessDateLoading, isSameBusinessDay,
  setVoidOpen, setRefundOpen,
}) {
  const [checkinOpen, setCheckinOpen] = React.useState(false);
  const customer = customerDetail?.customer;
  const isCheckedIn = !!(customer?.is_member || customer?.checked_in_at);
  return (
    <Card className="rounded-lg border-t-4 border-t-primary/60" data-testid="order-header-card-compact">
      <CardContent className="p-2.5">
        {order.package_name ? (
          <div
            className="mb-2 rounded-md border border-amber-300/70 bg-amber-50/80 px-2.5 py-1 flex items-center gap-2"
            data-testid="order-package-banner"
          >
            <PackagePlus className="w-3.5 h-3.5 text-amber-700" />
            <span className="text-[10px] uppercase tracking-wider text-amber-900/80">Package</span>
            <span className="text-xs font-medium text-amber-950">{order.package_name}</span>
            <span className="text-[10px] text-amber-800/80 ml-1">
              · one bill · package price applied across {(order.items || []).filter((it) => it.appointment_id).length || (order.items || []).length} service{((order.items || []).filter((it) => it.appointment_id).length || (order.items || []).length) === 1 ? "" : "s"}
            </span>
          </div>
        ) : null}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(260px,1fr)_auto_auto] gap-4 items-start">
          <div>
            <button onClick={onClose} className="text-xs text-muted-foreground hover:underline mb-1" data-testid="order-back">‹ Back to orders</button>
            {order.customer_id ? (
              <Link
                to={`/staff/brand/customers/${order.customer_id}`}
                className="font-semibold text-primary text-lg hover:underline block"
                data-testid="order-customer-name"
                title="Open client profile"
              >
                {order.customer_name || "Client"}
              </Link>
            ) : (
              <div className="font-semibold text-primary text-lg" data-testid="order-customer-name">{order.customer_name || "Walk-in"}</div>
            )}
            <div className="text-xs text-muted-foreground">{customerDetail?.whatsapp || customerDetail?.mobile || ""}</div>
            <div className="text-xs text-muted-foreground">{customerDetail?.email || ""}</div>
            {/* Front-Desk Check-In — prominent green button anchored
                directly under the client name. Visible for ANY guest
                that hasn't already completed check-in and hasn't been
                converted to a Member (once activated they don't need
                the wizard again). Opens the unified delivery picker
                (Email / WhatsApp / QR). Placed here — outside the
                narrower "has email" gate below — so the front desk
                can still choose WhatsApp or on-screen QR when the
                guest has no email on file. */}
            {order.customer_id
              && customerDetail?.customer
              && !customerDetail.customer.is_member
              && !customerDetail.customer.checked_in_at && (
              <div className="mt-2" data-testid="order-header-checkin-wrap">
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => setCheckinOpen(true)}
                  data-testid="checkin-guest-btn"
                >
                  <UserCheck className="w-3.5 h-3.5 mr-1" /> Check-In Guest
                </Button>
              </div>
            )}
            {/* Iter-365 (Feb 2026): the legacy "Send / Resend Invite"
                yellow envelope button and its dev-link chip were
                DELETED here. Guests now become Members the moment
                their first OTP sign-in lands at /portal/login — no
                invitation email required. */}
            {order.customer_id && isCheckedIn && (
              <div className="mt-2" data-testid="checked-in-badge">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 px-3 py-1 text-xs font-semibold">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Checked-In
                  {customer?.checked_in_at && (
                    <span className="text-emerald-700/70 font-normal">· {new Date(customer.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  )}
                </span>
              </div>
            )}
            {order.customer_id && customerDetail?.customer && (
              <div className="mt-1 flex items-center gap-2 text-xs flex-wrap" data-testid="customer-member-line">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider border font-semibold ${
                    customerDetail.customer.is_member
                      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                      : customerDetail.customer.membership_status === "pending"
                        ? "bg-amber-50 text-amber-800 border-amber-200"
                        : "bg-slate-100 text-slate-700 border-slate-300"
                  }`}
                  data-testid={
                    customerDetail.customer.is_member
                      ? "customer-member-badge"
                      : customerDetail.customer.membership_status === "pending"
                        ? "customer-pending-member-badge"
                        : "customer-non-member-badge"
                  }
                  title={
                    customerDetail.customer.is_member
                      ? "This guest is a Cocoon Member"
                      : customerDetail.customer.membership_status === "pending"
                        ? "Pending Member — will activate on first OTP sign-in at /portal/login"
                        : "Guest has not signed up as a Member yet — Redeem is locked"
                  }
                >
                  {customerDetail.customer.is_member
                    ? "Member"
                    : customerDetail.customer.membership_status === "pending"
                      ? "Pending Member"
                      : "Non-Member"}
                </span>
                <span className="text-muted-foreground">·</span>
                <span data-testid="customer-loyalty-balance">
                  <span className="font-semibold text-slate-800">{Number(customerDetail.customer.loyalty_points || 0).toLocaleString()}</span>
                  <span className="ml-1 text-muted-foreground">pts</span>
                </span>
              </div>
            )}
            {order.customer_id && (
              <LoyaltyPointsPanel
                order={order}
                customer={customerDetail?.customer}
                branch={branch}
                onChanged={refresh}
              />
            )}
          </div>

          <div className="text-xs space-y-0 leading-tight" data-testid="order-summary-compact">
            <div className="flex items-baseline gap-2">
              <span className="text-muted-foreground w-24 text-right">Order #:</span>
              <span className="font-mono" data-testid="order-number">{order.order_number || order.id.slice(0, 8)}</span>
              <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => openReceipt(order.id)} data-testid="print-invoice-btn"><Printer className="w-3 h-3" /></Button>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-muted-foreground w-24 text-right">Created:</span>
              <span data-testid="order-created-date">{fmtDate(order.created_at)}</span>
              <Button size="icon" variant="ghost" className="h-5 w-5" onClick={scrollToHistory} data-testid="order-history-btn" title="View history"><History className="w-3 h-3" /></Button>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-muted-foreground w-24 text-right">Treatment:</span>
              <TreatmentDateEditor order={order} onChanged={refresh} />
            </div>
            {(order.payments || []).length > 0 && (
              <div className="flex items-baseline gap-2" data-testid="order-last-payment-row">
                <span className="text-muted-foreground w-24 text-right">Last Payment:</span>
                <span>{fmtDateTime((order.payments[order.payments.length - 1] || {}).at)}</span>
              </div>
            )}
            <div className="flex items-baseline gap-2"><span className="text-muted-foreground w-24 text-right">Total:</span><span className="font-semibold" data-testid="order-total">{formatBranchMoney(branch, total)}</span></div>
            <div className="flex items-baseline gap-2"><span className="text-muted-foreground w-24 text-right">Amount Paid:</span><span data-testid="order-paid">{formatBranchMoney(branch, paid)}</span></div>
            <div className="flex items-baseline gap-2"><span className="text-muted-foreground w-24 text-right">Balance Due:</span><span className="font-semibold" data-testid="order-balance">{formatBranchMoney(branch, due)}</span></div>
            {paid - total > 0.009 ? (
              <div className="flex items-baseline gap-2" data-testid="order-credit-due-row">
                <span className="text-muted-foreground w-24 text-right">Credit Due:</span>
                <span className="font-semibold text-rose-600" data-testid="order-credit-due">{formatBranchMoney(branch, paid - total)}</span>
              </div>
            ) : null}
            <div className="flex items-baseline gap-2 pt-0.5"><span className="text-muted-foreground w-24 text-right">Status:</span><PaymentStatusPill order={order} /></div>
          </div>

          <div className="flex flex-col items-end gap-1.5 min-w-[220px]">
            <div className="w-full h-3 rounded-full bg-muted overflow-hidden border border-border" data-testid="order-progress-bar">
              <div className={`h-full ${order.status === "completed" ? "bg-emerald-500" : "bg-emerald-400"}`} style={{ width: `${progressPct}%`, transition: "width 250ms ease" }} />
            </div>
            <div className="text-[11px]" data-testid="order-status-label">
              Order is <span className="font-medium">{order.status === "completed" ? "Closed" : (order.status || "open")}</span>
            </div>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap justify-end">
              <Button size="sm" variant="outline" onClick={onClose} data-testid="save-for-later-btn" className="h-7 px-2 text-[11px]">Save For Later</Button>
              <Button size="sm" variant="outline" onClick={() => openReceipt(order.id)} data-testid="print-btn-2" className="h-7 px-2 text-[11px]"><Printer className="w-3 h-3 mr-1" /> Print Receipt</Button>
              {order.customer_id && (
                <Button size="sm" variant="outline" onClick={() => emailReceipt(order.id)} data-testid="email-receipt-btn" className="h-7 px-2 text-[11px]"><Mail className="w-3 h-3 mr-1" /> Email Receipt</Button>
              )}
              {order.status === "completed" && (
                businessDateLoading ? (
                  <Button size="sm" variant="outline" disabled data-testid="void-refund-loading">…</Button>
                ) : isSameBusinessDay ? (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setVoidOpen(true)} data-testid="void-order-btn">Void</Button>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => setRefundOpen(true)} data-testid="refund-order-btn">Refund</Button>
                )
              )}
              {/* Deposit-flow reversal (Feb 2026, Deposit vs Payment
                  Separation): an OPEN order carrying an active
                  deposit (any payment tagged `is_deposit`) MUST also
                  offer a reversal button. Same business day → Void
                  (money returns to source, invoice drops off Sales
                  reports); date rolled over → Refund. Closed orders
                  keep their existing button above; this block only
                  fires when the order is still open. */}
              {order.status === "open" && (order.payments || []).some((p) => p.is_deposit) && (
                businessDateLoading ? (
                  <Button size="sm" variant="outline" disabled data-testid="void-refund-loading">…</Button>
                ) : isSameBusinessDay ? (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setVoidOpen(true)} data-testid="void-deposit-btn">Void</Button>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => setRefundOpen(true)} data-testid="refund-deposit-btn">Refund</Button>
                )
              )}
              {order.status === "voided" && <Badge variant="outline" className="rounded-full text-[10px] bg-zinc-200 text-zinc-700 border-zinc-300">Voided</Badge>}
              {order.status === "refunded" && <Badge variant="outline" className="rounded-full text-[10px] bg-rose-100 text-rose-800 border-rose-200">Refunded</Badge>}
              {order.status === "merged" && (
                <Badge
                  variant="outline"
                  className="rounded-full text-[10px] bg-indigo-100 text-indigo-800 border-indigo-200"
                  data-testid="order-merged-badge"
                >Merged → #{order.merged_into_order_number}</Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
      <CheckInDialog
        open={checkinOpen}
        onOpenChange={setCheckinOpen}
        customer={customer}
        onSent={refresh}
      />
    </Card>
  );
}
